"use client";

import { useRef, useEffect, type ReactNode } from "react";
import { AssistantRuntimeProvider, ChatModelAdapter, useLocalRuntime } from "@assistant-ui/react";
import { usePresentationStore } from "@/state/presentationStore";
import { usePresentationDialogStore } from "@/state/presentationDialogStore";
import { outlineFramesToSelectedSlides } from "@/utils/slideOutline";

const INIT_SENTINEL = "__INIT_CHAT__";
const STREAM_CHAR_DELAY_MS = 4;

export const PresentationChatModelAdapter: ChatModelAdapter = {
  async *run({ messages }): AsyncGenerator<
    { content: Array<{ type: "text"; text: string }> },
    void,
    unknown
  > {
    const lastMessage = messages[messages.length - 1];
    let userInput = "";

    if (lastMessage?.content && Array.isArray(lastMessage.content)) {
      const textPart = lastMessage.content.find(
        (part) => typeof part === "object" && "text" in part && typeof part.text === "string"
      );
      if (textPart) userInput = (textPart as { text: string }).text;
    }
    const isInitialization = userInput === INIT_SENTINEL;

    try {
      window.dispatchEvent(new CustomEvent("ai-run-start"));
    } catch {
      // ignore
    }

    let response: string;

    if (isInitialization) {
      try {
        const { primaryCompany, selectedOutlineFrames } = usePresentationStore.getState();
        const selectedSlides = outlineFramesToSelectedSlides(selectedOutlineFrames);
        response = await usePresentationDialogStore.getState().initialize(
          primaryCompany?.companyTicker ?? "",
          primaryCompany?.companyExchange ?? "",
          primaryCompany?.companyName ?? "",
          selectedSlides,
        );
      } catch {
        response = "Sorry, I was unable to start the dialog. Please try again.";
      }
    } else if (userInput) {
      try {
        response = await usePresentationDialogStore.getState().interact(userInput);
      } catch {
        response = "Sorry, something went wrong processing your message. Please try again.";
      }
    } else {
      response = "Thanks for your reply. Use **Next** when you are ready to continue.";
    }

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    for (let i = 1; i <= response.length; i++) {
      if (i === 1) {
        try {
          window.dispatchEvent(new CustomEvent("ai-first-token"));
        } catch {
          // ignore
        }
      }
      yield {
        content: [{ type: "text", text: response.slice(0, i) }],
      };
      await delay(STREAM_CHAR_DELAY_MS);
    }
    try {
      window.dispatchEvent(new CustomEvent("ai-run-end"));
    } catch {
      // ignore
    }

    if (!isInitialization && userInput) {
      const currentStep = usePresentationStore.getState().currentStep;
      const userMessageCount = messages.filter((m: { role?: string }) => m.role === "user").length;
      usePresentationStore.getState().setAnswersByStep(currentStep, userMessageCount);
      usePresentationStore.getState().setDisabledButton(false);
    }
  },
};

export function ChatRuntimeProvider({ children }: { children: ReactNode }) {
  const { currentStep, setQuestionPhase, setDisabledButton } = usePresentationStore();

  const runtime = useLocalRuntime(PresentationChatModelAdapter, {
    initialMessages: [],
  });

  const initSentRef = useRef(false);
  useEffect(() => {
    if (initSentRef.current) return;
    if (currentStep !== 2) return;

    try {
      const thread = (runtime as { thread?: { append: (m: unknown) => void } })?.thread;
      if (thread && typeof thread.append === "function") {
        thread.append({
          role: "system",
          content: [{ type: "text", text: INIT_SENTINEL }],
          startRun: true,
        });
        initSentRef.current = true;
      }
    } catch {
      // ignore
    }
  }, [runtime, currentStep]);

  const openingCompleteRef = useRef(false);
  useEffect(() => {
    if (currentStep !== 2) return undefined;

    const handleRunEnd = () => {
      if (!initSentRef.current || openingCompleteRef.current) return;
      openingCompleteRef.current = true;
      setQuestionPhase("complete");
      setDisabledButton(false);
    };

    window.addEventListener("ai-run-end", handleRunEnd);
    return () => {
      window.removeEventListener("ai-run-end", handleRunEnd);
    };
  }, [currentStep, setQuestionPhase, setDisabledButton]);

  return (
    <AssistantRuntimeProvider key={`runtime-${currentStep}`} runtime={runtime}>
      <div className="h-full min-h-0 text-sm leading-snug [&_.aui-md]:text-sm">
        {children}
      </div>
    </AssistantRuntimeProvider>
  );
}
